// FILE: SideChatPanel.logic.ts
// Purpose: The decisions behind opening a side chat — which agents may run one,
//          what the child thread inherits, and how the anchoring quote is worded.
// Layer: pure logic (no React, no DOM)
// Depends on: contracts types and the shared side-chat role

import type {
  CommandId,
  IsoDateTime,
  ManagedAgent,
  ModelSelection,
  OrchestrationCommand,
  ProjectId,
  ProviderInteractionMode,
  RuntimeMode,
  ThreadEnvironmentMode,
  ThreadId,
} from "@taskin-code/contracts";
import { canRunSideChat } from "@taskin-code/shared/managedAgentMode";
import { buildAgentModelSelection } from "@taskin-code/shared/managedAgentModelOptions";
import { SIDE_CHAT_THREAD_ROLE } from "@taskin-code/shared/sideChat";

import {
  appendQuotedContextsToPrompt,
  type MessageQuotedContextSelection,
} from "~/lib/quotedContext";

/** The parent's fields a side chat inherits. Structural so a test needs no whole thread. */
export interface SideChatParentThread {
  readonly id: ThreadId;
  readonly projectId: ProjectId;
  readonly runtimeMode: RuntimeMode;
  readonly interactionMode: ProviderInteractionMode;
  readonly envMode?: ThreadEnvironmentMode | null | undefined;
  readonly branch: string | null;
  readonly worktreePath: string | null;
  readonly associatedWorktreePath?: string | null | undefined;
  readonly associatedWorktreeBranch?: string | null | undefined;
  readonly associatedWorktreeRef?: string | null | undefined;
}

/**
 * The agents offered in the picker.
 *
 * The flag and the mode are applied here; team visibility is not, because the
 * list handed in is already what the server decided this user may see
 * (`listVisibleForUser`), and filtering by team again on the client would be a
 * second, weaker copy of a rule that already has one home. The mode *is* applied
 * client-side because the list deliberately still carries subagent-only agents —
 * the transcript labels delegation rows from it — so the picker has to do the
 * narrowing the list does not. An empty result is a real state: the action is
 * disabled, with no fallback to the parent thread's own model, because "some
 * agent, unspecified" is not a thing an admin approved.
 */
export function resolveSideChatAgents(
  agents: ReadonlyArray<ManagedAgent>,
): ReadonlyArray<ManagedAgent> {
  return agents.filter((agent) => agent.sideChatEnabled && canRunSideChat(agent.mode));
}

/**
 * A side chat runs entirely on the agent's own definition — including how hard
 * it thinks. There is no composer here to have picked an effort, so the pin is
 * the only thing that could set one.
 */
export function buildSideChatModelSelection(agent: ManagedAgent): ModelSelection {
  return buildAgentModelSelection(agent);
}

const SIDE_CHAT_TITLE_MAX_LENGTH = 60;

/**
 * A title taken from the quoted text rather than from the agent's name, so a
 * thread the user opens twice over different fragments is tellable apart in the
 * sidebar.
 */
export function buildSideChatTitle(quotedText: string): string {
  const collapsed = quotedText.replace(/\s+/gu, " ").trim();
  if (collapsed.length === 0) return "Side chat";
  if (collapsed.length <= SIDE_CHAT_TITLE_MAX_LENGTH) return collapsed;
  return `${collapsed.slice(0, SIDE_CHAT_TITLE_MAX_LENGTH - 1).trimEnd()}…`;
}

/**
 * The first message: what the user asked, with the quote appended.
 *
 * Goes through the composer's own quoting system rather than inventing a second
 * format. Two reasons: a side chat runs in its own session with no history of the
 * parent conversation, so the quote has to be materialized into the prompt or a
 * bare "what does this mean?" arrives pointing at nothing; and the rendered
 * message parses back out through the same reader every other quoted message
 * does, so the transcript shows a quote chip instead of raw tags.
 */
export function buildSideChatSeedPrompt(input: {
  readonly quote: MessageQuotedContextSelection;
  readonly question: string;
}): string {
  return appendQuotedContextsToPrompt(input.question, [input.quote]);
}

/**
 * The child thread, inheriting the parent field by field.
 *
 * The worktree is shared, not copied — the same choice `agent_delegate_open`
 * makes, and for the same reason: a side chat asked about a change has to be able
 * to see it. Two sessions writing to one worktree can collide; that is accepted
 * here as it already is for delegation.
 */
export function buildSideChatThreadCreateCommand(input: {
  readonly commandId: CommandId;
  readonly threadId: ThreadId;
  readonly parent: SideChatParentThread;
  readonly agent: ManagedAgent;
  readonly title: string;
  readonly createdAt: IsoDateTime;
}): Extract<OrchestrationCommand, { type: "thread.create" }> {
  const { parent, agent } = input;
  return {
    type: "thread.create",
    commandId: input.commandId,
    threadId: input.threadId,
    projectId: parent.projectId,
    title: input.title,
    modelSelection: buildSideChatModelSelection(agent),
    runtimeMode: parent.runtimeMode,
    interactionMode: parent.interactionMode,
    envMode: parent.envMode ?? "local",
    branch: parent.branch,
    worktreePath: parent.worktreePath,
    associatedWorktreePath: parent.associatedWorktreePath ?? null,
    associatedWorktreeBranch: parent.associatedWorktreeBranch ?? null,
    associatedWorktreeRef: parent.associatedWorktreeRef ?? null,
    parentThreadId: parent.id,
    subagentAgentId: agent.id,
    subagentNickname: agent.name,
    subagentRole: SIDE_CHAT_THREAD_ROLE,
    createdAt: input.createdAt,
  };
}
